import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearning } from '../hooks/useLearning';
import ProgressBar from '../components/learning/ProgressBar';
import ScrambleExercise from '../components/learning/ScrambleExercise';
import FillBlankExercise from '../components/learning/FillBlankExercise';
import FreeProductionExercise from '../components/learning/FreeProductionExercise';
import ConversationExercise from '../components/learning/ConversationExercise';
import FeedbackScreen from '../components/learning/FeedbackScreen';
import SessionSummary from '../components/learning/SessionSummary';

type Phase = 'ready' | 'exercise' | 'feedback' | 'summary';

interface SessionStats {
  correct: number;
  wrong: number;
  xpEarned: number;
}

const topicKeys: Record<string, string> = {
  word_order: 'topicWordOrder',
  verb_conjugation: 'topicVerbConjugation',
  articles: 'topicArticles',
  prepositions: 'topicPrepositions',
  vocabulary: 'topicVocabulary',
  completeness: 'topicCompleteness',
  general: 'topicGeneral',
};

export default function LearningPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, progress, exercise, loading, exerciseLoading, submitAnswer, nextExercise } = useLearning();
  const [phase, setPhase] = useState<Phase>('ready');
  const [feedbackData, setFeedbackData] = useState<{
    correct: boolean;
    partiallyCorrect?: boolean;
    feedback: string;
    correctAnswer: string;
    xpEarned: number;
    levelUp?: string;
  } | null>(null);
  const [sessionStats, setSessionStats] = useState<SessionStats>({ correct: 0, wrong: 0, xpEarned: 0 });
  const [exerciseCount, setExerciseCount] = useState(0);

  const EXERCISES_PER_SESSION = 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-2xl mb-2">📚</p>
          <p className="text-gray-500">{t('learning.loading')}</p>
        </div>
      </div>
    );
  }

  const handleStartSession = async () => {
    setSessionStats({ correct: 0, wrong: 0, xpEarned: 0 });
    setExerciseCount(0);
    setPhase('exercise');
  };

  const handleSubmitAnswer = async (answer: string) => {
    if (!exercise) return;

    try {
      const result = await submitAnswer(exercise.exerciseId, answer);
      setFeedbackData({
        correct: result.correct,
        partiallyCorrect: result.partiallyCorrect,
        feedback: result.feedback,
        correctAnswer: result.correctAnswer,
        xpEarned: result.xpEarned,
        levelUp: result.levelUp,
      });
      setSessionStats(prev => ({
        correct: prev.correct + (result.correct ? 1 : 0),
        wrong: prev.wrong + (result.correct ? 0 : 1),
        xpEarned: prev.xpEarned + result.xpEarned,
      }));
      setExerciseCount(prev => prev + 1);
      setPhase('feedback');
    } catch (err) {
      console.error('Submit failed:', err);
    }
  };

  const handleNext = async () => {
    if (exerciseCount >= EXERCISES_PER_SESSION) {
      setPhase('summary');
    } else {
      await nextExercise();
      setPhase('exercise');
    }
  };

  // ─── Ready Screen ────────────────────────────────────────────
  if (phase === 'ready') {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {profile && progress && (
          <ProgressBar
            currentLevel={profile.currentLevel}
            xpProgress={progress.xpProgress}
            xpNeeded={progress.xpNeeded}
            nextLevel={progress.nextLevel}
            totalXp={profile.xp}
          />
        )}

        {profile && profile.streak > 0 && (
          <div className="text-center bg-orange-50 rounded-xl p-3">
            <p className="text-lg">🔥 {t('learning.streak', { count: profile.streak })}</p>
          </div>
        )}

        {profile && Object.keys(profile.stats).length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 mb-3">{t('learning.accuracy')}</h3>
            <div className="space-y-2">
              {Object.entries(profile.stats).map(([topic, stat]) => {
                const accuracy = stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0;
                const labelKey = topicKeys[topic] || 'topicGeneral';
                return (
                  <div key={topic} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24">{t(`learning.${labelKey}`)}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${accuracy >= 70 ? 'bg-green-400' : accuracy >= 40 ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${accuracy}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 w-10 text-right">{accuracy}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={handleStartSession}
          disabled={!exercise && !exerciseLoading}
          className="w-full py-5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold rounded-2xl text-xl shadow-lg hover:shadow-xl active:scale-98 transition disabled:opacity-50"
        >
          {exercise ? t('learning.start') : t('learning.noExercises')}
        </button>

        {!exercise && (
          <p className="text-center text-sm text-gray-400">
            {t('learning.askAdmin')}
          </p>
        )}
      </div>
    );
  }

  // ─── Exercise Screen ─────────────────────────────────────────
  if (phase === 'exercise') {
    if (exerciseLoading || !exercise) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">{t('learning.preparing')}</p>
        </div>
      );
    }

    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-400">
            {exerciseCount + 1} / {EXERCISES_PER_SESSION}
          </span>
          <span className="text-sm text-gray-400">
            {exercise.topic}
          </span>
          <span className="px-2 py-1 bg-indigo-100 text-indigo-600 rounded text-xs font-medium">
            Lv.{exercise.level}
          </span>
        </div>

        {exercise.type === 'scramble' && exercise.words && (
          <ScrambleExercise
            prompt={exercise.prompt}
            words={exercise.words}
            hints={exercise.hints}
            onSubmit={handleSubmitAnswer}
          />
        )}
        {exercise.type === 'fill-blank' && exercise.template && (
          <FillBlankExercise
            prompt={exercise.prompt}
            template={exercise.template}
            hints={exercise.hints}
            onSubmit={handleSubmitAnswer}
          />
        )}
        {exercise.type === 'free-production' && (
          <FreeProductionExercise
            prompt={exercise.prompt}
            hints={exercise.hints}
            onSubmit={handleSubmitAnswer}
          />
        )}
        {exercise.type === 'conversation' && (
          <ConversationExercise
            prompt={exercise.prompt}
            hints={exercise.hints}
            onSubmit={handleSubmitAnswer}
          />
        )}
      </div>
    );
  }

  // ─── Feedback Screen ─────────────────────────────────────────
  if (phase === 'feedback' && feedbackData) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        <FeedbackScreen
          correct={feedbackData.correct}
          partiallyCorrect={feedbackData.partiallyCorrect}
          feedback={feedbackData.feedback}
          correctAnswer={feedbackData.correctAnswer}
          xpEarned={feedbackData.xpEarned}
          levelUp={feedbackData.levelUp}
          onNext={handleNext}
        />
      </div>
    );
  }

  // ─── Session Summary ─────────────────────────────────────────
  if (phase === 'summary') {
    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        <SessionSummary
          correct={sessionStats.correct}
          wrong={sessionStats.wrong}
          xpEarned={sessionStats.xpEarned}
          streak={profile?.streak || 0}
          onClose={() => navigate('/')}
        />
      </div>
    );
  }

  return null;
}
